/**
 * SnapshotPanel — Document version history using snappr-lib.
 *
 * Composes:
 * - snappr-lib (SnapshotManager): createSnapshot, listSnapshots, revertToSnapshot
 * - widgetr-lib (Input, Button): Form fields and action buttons
 *
 * The panel delegates ALL snapshot logic to snappr-lib's SnapshotManager
 * exposed via ExpressrContext. This component is a thin presentation layer.
 */

import { useState, useEffect, useCallback } from 'react';
import { useExpressrContext } from '../context/ExpressrContext.js';

// Inline type for browser compatibility (avoids importing Node-only snappr-lib)
interface Snapshot {
  id: string;
  message: string;
  timestamp: Date;
  author?: string;
  commitHash: string;
  filesChanged: string[];
}

export function SnapshotPanel() {
  const { activeDocument, snapshots, config } = useExpressrContext();
  const [snapshotList, setSnapshotList] = useState<Snapshot[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [confirmRestore, setConfirmRestore] = useState<string | null>(null);

  // Load snapshots for active document via snappr-lib
  const loadSnapshots = useCallback(async () => {
    if (!activeDocument) { setSnapshotList([]); return; }
    try {
      const list = await snapshots.listSnapshots({ processId: activeDocument.id, tool: 'expressr' });
      setSnapshotList(list);
    } catch {
      setSnapshotList([]);
    }
  }, [activeDocument, snapshots]);

  useEffect(() => { loadSnapshots(); }, [loadSnapshots]);

  if (!activeDocument) {
    return (
      <div style={{ padding: 16, fontSize: 12, opacity: 0.6, height: '100%' }}>
        Select a document to view snapshots.
      </div>
    );
  }

  const handleCreate = async () => {
    const msg = newMessage.trim() || 'Manual snapshot';
    try {
      await snapshots.createSnapshot(
        activeDocument.id,
        'expressr',
        msg,
        {
          tool: 'expressr',
          command: 'snapshot',
          author: config?.user?.name,
          errorsBeforeFix: 0,
          errorsAfterFix: 0,
          errorsFixed: 0,
          duration: 0,
          projectPath: '',
          branch: '',
        }
      );
      setNewMessage('');
      await loadSnapshots();
    } catch {
      // snappr may not have git context
    }
  };

  const handleRestore = async (snapshotId: string) => {
    try {
      await snapshots.revertToSnapshot({ snapshotId, createBackup: true });
      setConfirmRestore(null);
      await loadSnapshots();
    } catch {
      // revert may fail without git context
    }
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return d.toLocaleString();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Snapshots</span>
        <div style={{ fontSize: 11, marginTop: 2, opacity: 0.6 }}>
          {snapshotList.length} snapshot{snapshotList.length !== 1 ? 's' : ''} for &ldquo;{activeDocument.title}&rdquo;
        </div>
      </div>

      {/* Create — would use widgetr Input + Button */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            placeholder="Snapshot description..."
            style={{ flex: 1, width: '100%' }}
          />
          <button onClick={handleCreate}>Save</button>
        </div>
      </div>

      {/* Snapshot list — snappr-lib listSnapshots result */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {snapshotList.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', opacity: 0.5, fontSize: 12 }}>
            No snapshots yet. Create one to save a version of your document.
          </div>
        ) : (
          snapshotList.map((snap) => (
            <div key={snap.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-light, #1e1e38)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {snap.message}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 3 }}>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>{formatTime(snap.timestamp)}</span>
                    {snap.author && <span style={{ fontSize: 11, opacity: 0.6 }}>by {snap.author}</span>}
                    {snap.commitHash !== 'unknown' && (
                      <span style={{ fontSize: 11, opacity: 0.6, fontFamily: 'monospace' }}>{snap.commitHash.slice(0, 7)}</span>
                    )}
                  </div>
                </div>
                <div>
                  {confirmRestore === snap.id ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => handleRestore(snap.id)} style={{ fontSize: 10 }}>Confirm</button>
                      <button onClick={() => setConfirmRestore(null)} style={{ fontSize: 10 }}>Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmRestore(snap.id)} style={{ fontSize: 10 }}>Restore</button>
                  )}
                </div>
              </div>

              {/* File changes from snappr-lib */}
              {snap.filesChanged.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 11, opacity: 0.5 }}>
                  {snap.filesChanged.length} file{snap.filesChanged.length !== 1 ? 's' : ''} changed
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
